import '@/lib/leaflet-global'; // Make L global BEFORE leaflet-rotate
import 'leaflet-rotate';
import { MapContainer, Polyline, useMap } from 'react-leaflet';
import { useEffect, useRef, memo } from 'react';
import { RESOURCE_CATEGORIES, MARKER_COLORS } from './constants';
import {
  MapUpdater, ClusterLayer, UserLocationMarker,
  RoutingControl, DropPinLayer, FollowUser, DestinationMarker, CityMarkers, FuelStationMarkers, TouchFix, EnableRotation, IncidentMarkers
} from './MapComponents';
import { VectorTileLayer } from './VectorTileLayer';
// Lazy load heatmap/closure layers to avoid crash
let HeatmapLayer = () => null;
let RoadClosureMarkers = () => null;
let EvacuationRouteLines = () => null;
try {
  const hm = require('./HeatmapLayer');
  HeatmapLayer = hm.HeatmapLayer;
  const rc = require('./RoadClosureLayer');
  RoadClosureMarkers = rc.RoadClosureMarkers;
  EvacuationRouteLines = rc.EvacuationRouteLines;
} catch (e) {
  console.warn('Crisis layers failed to load:', e);
}

const CachedRouteLine = ({ coords }) => {
  if (!coords?.length) return null;
  const positions = coords
    .filter(c => c && Number.isFinite(c.lat) && Number.isFinite(c.lng))
    .map(c => [c.lat, c.lng]);
  if (positions.length < 2) return null;
  return <Polyline positions={positions} pathOptions={{ color: '#a855f7', weight: 5, opacity: 0.45 }} />;
};

// Captures the Leaflet map instance and exposes it to parent
const MapRefSetter = ({ onMapRef }) => {
  const map = useMap();
  useEffect(() => {
    if (onMapRef) onMapRef(map);
    return () => { if (onMapRef) onMapRef(null); };
  }, [map, onMapRef]);
  return null;
};

const isValidCoord = (c) => c != null && Number.isFinite(c.lat) && Number.isFinite(c.lng);

const ResourceMapInner = ({ resources, filterCategory, userLocation, onGetDirections, onAddResource, onAddWaypoint, navDestination, navSteps, onNavSteps, onNavClear, dropPinActive, routeCoords, onRouteCoords, onSpeedLimits, hasCachedRoute, isOffline, transportMode, fuelStations, showFuelStations, fullHeight, hideControls, onMapRef, accuracy, heatmapPoints, showHeatmap, roadClosures, showClosures, onUpvoteClosure, evacuationRoutes, showEvacRoutes, onUserInteract, incidents, onIncidentVote }) => {
  const validUser = isValidCoord(userLocation);
  const validDest = isValidCoord(navDestination);
  const center = validUser ? [userLocation.lat, userLocation.lng] : [54.5, -2.5];
  const zoom = validUser ? (isOffline ? 13 : 12) : 6;
  const isNavigating = !!navDestination;
  const needsOSRM = isNavigating && !routeCoords;
  const hadRouteRef = useRef(false);
  if (routeCoords) hadRouteRef.current = true;
  // If we need routing and previously had a route, it's a reroute — suppress toast noise
  const isReroute = needsOSRM && hadRouteRef.current;

  return (
    <div
      className="relative overflow-hidden"
      style={{
        zIndex: 0,
        height: fullHeight ? '100%' : '450px',
        minHeight: '400px',
        borderRadius: fullHeight ? 0 : '0.75rem',
        border: fullHeight ? 'none' : '1px solid var(--border)',
        touchAction: 'none',
      }}
      data-testid="crisis-resource-map"
    >
      <MapContainer
        center={center}
        zoom={zoom}
        maxZoom={18}
        minZoom={3}
        rotate={true}
        bearing={0}
        style={{ width: '100%', height: '100%', background: '#f2efe9' }}
        zoomControl={false}
        attributionControl={true}
        className="crisis-dark-map"
      >
        <MapRefSetter onMapRef={onMapRef} />
        <EnableRotation />
        <VectorTileLayer />
        <TouchFix />
        {!isNavigating && <MapUpdater center={center} zoom={zoom} />}
        {!isNavigating && <ClusterLayer resources={resources} onGetDirections={onGetDirections} />}
        <UserLocationMarker position={validUser ? userLocation : null} accuracy={accuracy} />
        <CityMarkers onNavigate={onGetDirections} visible={!isNavigating} />
        <FuelStationMarkers stations={fuelStations} visible={showFuelStations} onNavigate={onGetDirections} />
        {isNavigating && validDest && <DestinationMarker position={navDestination} name={navDestination?.name} />}
        {isNavigating && validUser && <FollowUser position={userLocation} active={true} steps={navSteps} routeCoords={routeCoords} onUserInteract={onUserInteract} />}
        <DropPinLayer active={dropPinActive} onGetDirections={onGetDirections} onAddResource={onAddResource} onAddWaypoint={onAddWaypoint} isNavigating={!!navDestination} />
        {isNavigating && routeCoords && <CachedRouteLine coords={routeCoords} />}
        {needsOSRM && validUser && (
          <RoutingControl from={userLocation} to={navDestination} onSteps={onNavSteps} onClear={onNavClear} onRouteCoords={onRouteCoords} onSpeedLimits={onSpeedLimits} transportMode={transportMode} quiet={isReroute} />
        )}
        <HeatmapLayer points={heatmapPoints} visible={showHeatmap} />
        <RoadClosureMarkers closures={roadClosures} visible={showClosures} onUpvote={onUpvoteClosure} />
        <EvacuationRouteLines routes={evacuationRoutes} visible={showEvacRoutes} />
        <IncidentMarkers incidents={incidents} visible={true} onVote={onIncidentVote} />
      </MapContainer>

      {/* Zoom controls — only when parent doesn't provide its own */}
      {!hideControls && (
        <div className="absolute right-3 top-3 z-[400] flex flex-col gap-1" data-testid="map-zoom-controls">
          <button
            onClick={() => document.querySelector('.crisis-dark-map .leaflet-container')?.__leaflet_map?.zoomIn() || document.querySelector('.leaflet-control-zoom-in')?.click()}
            className="w-10 h-10 rounded-lg bg-background/90 backdrop-blur-md border border-border/40 shadow-lg flex items-center justify-center text-foreground hover:bg-background transition-colors text-lg font-bold"
            data-testid="map-zoom-in"
          >+</button>
          <button
            onClick={() => document.querySelector('.crisis-dark-map .leaflet-container')?.__leaflet_map?.zoomOut() || document.querySelector('.leaflet-control-zoom-out')?.click()}
            className="w-10 h-10 rounded-lg bg-background/90 backdrop-blur-md border border-border/40 shadow-lg flex items-center justify-center text-foreground hover:bg-background transition-colors text-lg font-bold"
            data-testid="map-zoom-out"
          >-</button>
        </div>
      )}

      {/* Compact legend — bottom-left floating, only in non-fullscreen mode */}
      {!hideControls && !isNavigating && !fullHeight && (
        <div className="absolute bottom-2 left-2 z-[400] flex flex-wrap gap-x-2 gap-y-0.5 px-2 py-1.5 rounded-lg bg-background/85 backdrop-blur-md border border-border/30 max-w-[70%]">
          {RESOURCE_CATEGORIES.filter(c => c.value !== 'other').slice(0, 6).map(c => (
            <div key={c.value} className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: MARKER_COLORS[c.value] || MARKER_COLORS.other }} />
              <span className="text-[8px] text-muted-foreground">{c.label}</span>
            </div>
          ))}
          {showFuelStations && (
            <div className="flex items-center gap-1">
              <span className="w-2 h-2 rounded flex-shrink-0" style={{ backgroundColor: '#ef4444' }} />
              <span className="text-[8px] text-muted-foreground">Fuel</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default memo(ResourceMapInner);
export const ResourceMap = memo(ResourceMapInner);
